import React, { useState } from "react";
import { PencilIcon } from "lucide-react";
import InputInline from "../components/InputInline";

interface EditableTextCellProps {
  value: string;
  className?: string;
  onClick?: () => void;
  onEdit?: (newValue: string) => void;
  readOnly?: boolean;
}

const EditableTextCell: React.FC<EditableTextCellProps> = ({
  value,
  className = "",
  onClick,
  onEdit,
  readOnly = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);

  const handleTextClick = (e: React.MouseEvent) => {
    // Stop propagation to prevent triggering edit click
    e.stopPropagation();
    // Only trigger onClick if not clicking on edit icon
    if (!(e.target as HTMLElement).closest(".edit-icon")) {
      onClick?.();
    }
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!readOnly && onEdit) {
      setIsEditing(true);
    }
  };

  const handleContainerClick = (e: React.MouseEvent) => {
    // Only trigger edit if clicking on container itself (not on text or icon)
    const target = e.target as HTMLElement;
    if (
      target === e.currentTarget ||
      (!target.closest(".cursor-pointer") && !target.closest(".edit-icon"))
    ) {
      handleEditClick(e);
    }
  };

  const handleFinish = (newValue: string) => {
    if (onEdit && newValue !== value) {
      onEdit(newValue);
    }
    // Always reset editing state to return to normal view without border
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="flex-1 min-w-0 flex items-center">
        <InputInline
          value={value}
          onFinish={handleFinish}
          onBlur={() => {
            // Reset editing state when blur to return to normal view without border
            setIsEditing(false);
          }}
          variant="inline"
          autoFocus
        />
        <div className="w-4" />
      </div>
    );
  }

  // Always render PencilIcon to reserve space and prevent layout shift.
  // Visibility is controlled via CSS `.icon-action` class in ActionableField.
  const showEditIcon = !readOnly && !!onEdit;

  return (
    <div className={`flex flex-1 min-w-0 ${className}`} onClick={handleContainerClick}>
      <div className="flex items-center hover:bg-gray-100 rounded max-w-full">
        <div
          className="cursor-pointer inline-block max-w-full truncate underline decoration-gray-200 underline-offset-2 hover:no-underline px-1 py-0.5 transition-colors"
          onClick={handleTextClick}
        >
          {value}
        </div>
        {showEditIcon && (
          <PencilIcon
            className="icon-action w-4 h-4 shrink-0 text-gray-400 hover:text-gray-600 cursor-pointer ml-1 edit-icon mr-1"
            onClick={handleEditClick}
          />
        )}
      </div>
    </div>
  );
};

export default EditableTextCell;
